/* eslint-disable max-lines */

import {
  EC2_ELASTIC_IP,
  EC2_IMAGE,
  EC2_INSTANCE,
  EC2_INTERNET_GATEWAY,
  EC2_KEY_PAIR,
  EC2_LAUNCH_TEMPLATE,
  EC2_LOAD_BALANCER,
  EC2_NETWORK_ACL,
  EC2_NETWORK_INTERFACE,
  EC2_PLACEMENT_GROUP,
  EC2_ROUTE_TABLE,
  EC2_SECURITY_GROUP,
  EC2_SNAPSHOT,
  EC2_SUBNET,
  EC2_VOLUME,
  EC2_VPC,
  IAM_GROUP,
  IAM_POLICY,
  IAM_ROLE,
  IAM_USER,
  LAMBDA_ALIAS,
  LAMBDA_EVENT_SOURCE_MAPPING,
  LAMBDA_FUNCTION,
  LAMBDA_LAYER,
  LAMBDA_VERSION,
  RDS_DB_CLUSTER,
  RDS_DB_CLUSTER_PARAMETER_GROUP,
  RDS_DB_INSTANCE,
  RDS_DB_PARAMETER_GROUP,
  RDS_DB_PROXY,
  RDS_DB_SECURITY_GROUP,
  RDS_DB_SUBNET_GROUP,
  RDS_OPTION_GROUP,
  ROUTE53_CIDR,
  ROUTE53_HEALTH_CHECK,
  ROUTE53_HOSTED_ZONE,
  ROUTE53_QUERY_LOGGING_CONFIG,
  ROUTE53_REUSABLE_DELEGATION_SET,
  ROUTE53_TRAFFIC_POLICY,
  ROUTE53_TRAFFIC_POLICY_INSTANCE,
  S3_ACCESS_POINT,
  S3_BUCKET,
} from '@/lib/types/resourceDefs';
import {
  IMPACTED_BY_UNMANAGED,
  IMPACTS_MANAGED,
  MANAGED,
  NOT_MANAGED,
} from '@/routes/_root/coverage/-components/CoverageTable/utils';

// This file is used as the source of truth for all other translations files format.
export const enUSMessages = {
  global: {
    anyshift: 'Anyshift',
    error: {
      title: 'Oops! An Error occurred...',
      goBack: 'Go back to home',
      stuck: 'Stuck? Contact us at: ',
      toggleTrace: 'Toggle trace',
      message: 'Message:',
      stack: 'Stack:',
      resourceNotFound: 'Resource not found',
    },
    menu: {
      title: 'Menu',
      signOut: 'Sign out',
      signOutError: 'Failed to sign out',
      search: 'Search',
      coverage: 'Coverage',
      configuration: 'Configuration',
      myProfile: 'My Profile',
      pullRequest: 'Pull Requests',
      settings: 'Settings',
      themeSettings: {
        title: 'Theme Settings',
        light: 'Light Mode',
        dark: 'Dark Mode',
        system: 'System Mode',
      },
      integrations: 'Integrations',
      catalog: 'Ask Annie',
      welcome: 'Welcome {username}',
      admin: 'Admin',
      incidents: 'Root Cause Analysis',
      gettingStarted: 'Getting started',
    },
    status: {
      active: 'Active',
      inactive: 'Inactive',
      error: 'Error',
      readyToConnect: 'Ready to Connect',
      connected: 'Connected',
      notConnected: 'Not Connected',
    },
    breadcrumb: {
      home: 'Home',
      resource: 'Resource',
      profile: 'My profile',
      coverage: 'Cloud-to-code coverage',
      configuration: 'Configuration',
      pullRequests: 'My pull requests',
      newAWSCredentials: 'New AWS credentials',
      editAWSCredentials: 'Edit AWS credentials',
      newTerraformState: 'New Terraform integration',
      editTerraformState: 'Edit Terraform integration',
      newAWSRole: 'New AWS role',
      editAWSRole: 'Edit AWS role',
      integrations: 'Integrations',
      catalog: 'Use Cases',
      hcpCloud: 'HCP Cloud',
      newHCPCloudToken: 'New HCP Cloud token',
      editHCPCloudToken: 'Edit HCP Cloud token',
      newGCPServiceAccount: 'New GCP service account',
      editGCPServiceAccount: 'Edit GCP service account',
      newDatadogApiCredentials: 'New Datadog API credentials',
      editDatadogApiCredentials: 'Edit Datadog API credentials',
      newPagerDutyToken: 'New PagerDuty token',
      editPagerDutyToken: 'Edit PagerDuty token',
      newPagerDutyWebhook: 'New PagerDuty webhook',
      editPagerDutyWebhook: 'Edit PagerDuty webhook',
      admin: 'Admin',
      gettingStarted: 'Getting started',
      gettingStartedSlackChannels: 'Make Annie On-Call',
      incidents: 'Root Cause Analysis',
    },
    noValue: '-',
    noResults: 'No results',
    notAvailable: 'N/A',
    previous: 'Previous',
    next: 'Next',
    copiedToClipboard: 'Copied to clipboard',
    paginationOf: '{start} - {end} of {total} results',
    components: {
      dateTimePicker: {
        placeholder: 'Pick date and time',
      },
    },
    slack: {
      installationFailed: 'Failed to install Slack app, try again',
    },
  },
  githubCallback: {
    loader: {
      title: 'Installing Anyshift Github App',
      subtitle: 'Do not close the tab',
    },
    installationError:
      'Failed to create Github configuration, try to uninstall and install the app again',
    installationSuccess: 'Github configuration created',
    authenticationError: 'Failed to authenticate with Github, try again',
  },
  auth: {
    signUp: {
      title: 'Create your account',
      alreadyHaveAccount: 'Already have an account? ',
      signIn: 'Sign in',
      form: {
        email: {
          label: 'Email',
          placeholder: 'name@company.com',
          error: 'Should be a valid email',
        },
        password: {
          label: 'Password',
          placeholder: '••••••••',
          error: 'Should be at least {min} characters',
        },
        submit: {
          label: 'Sign up',
          pending: 'Signing up...',
          error: 'Failed to sign up, try again',
        },
      },
    },
    signIn: {
      title: 'Sign in to your account',
      noAccount: "Don't have an account yet? ",
      signUp: 'Sign up',
      forgotPassword: 'Forgot password?',
      form: {
        email: {
          label: 'Email',
          placeholder: 'name@company.com',
          error: 'Should be a valid email',
        },
        password: {
          label: 'Password',
          placeholder: '••••••••',
          error: 'Should be at least {min} characters',
        },
        submit: {
          label: 'Sign in',
          pending: 'Signing in...',
          error: 'Failed to sign in, check your credentials and try again',
        },
        rememberMe: 'Remember me',
      },
    },
    resetPasswordOnInvitation: {
      title: 'Welcome to Anyshift',
      subtitle: "First, let's set up your account",
      error: 'Failed to authenticate, check that the link is still valid',
      expired: {
        title: 'Invitation Link Expired',
        description:
          'Your invitation link has expired or is no longer valid. You can request a new password reset link or sign in if you already have an account.',
        resetPassword: 'Request New Password Reset',
        signIn: 'Sign In Instead',
      },
    },
    resetPasswordOnForgotPassword: {
      title: 'Reset your password',
    },
    resetPassword: {
      form: {
        email: 'Email',
        password: {
          label: 'New password',
          placeholder: '••••••••',
          error: 'Should be at least {min} characters',
        },
        submit: {
          label: 'Update',
          pending: 'Updating...',
          error: 'Failed to reset password, try again',
          success: 'Password updated',
        },
      },
    },
    notVerified: {
      title: 'Please, check your email for the verification link',
      subtitle:
        'You need to verify your email address to complete the signup process. Please check your email and click on the verification link.',
    },
    forgotPassword: {
      title: 'Forgot your password?',
      form: {
        email: {
          label: 'You will receive an email with a password recovery link',
          placeholder: 'name@company.com',
          error: 'Should be a valid email',
        },
        submit: {
          label: 'Send recovery link',
          pending: 'Sending recovery link...',
          error: 'Failed to send recovery link, check the email and try again',
          success: 'Recovery link to your email',
        },
      },
    },
    slackNotLinked: {
      title: 'Thank you for installing Anyshift!',
      welcomeMessage:
        'Thank you for installing Anyshift on your slack workspace {workspace}',
      description: 'Sign in to link your project to the Slack app.',
      signInCta: 'Sign in to link your project',
    },
  },
  resource: {
    host: {
      hostedResources: {
        title: 'Hosted resources',
        resourceType: 'Resource type',
        service: 'Service',
        resourceId: 'Resource identifier',
      },
    },
    instance: {
      details: {
        title: 'Properties',
        region: {
          label: '🌎 AWS Region',
          description:
            'Click the link to explore resources we found in this region',
        },
        account: {
          label: '👤 AWS Account',
          description:
            'Click the link to explore resources we found in this account',
        },
        service: {
          label: '⚙️ AWS Service',
          description:
            'Click the link to explore resources we found in this service',
        },
        resourceType: {
          label: '📦 Resource type',
        },
      },
      definition: {
        title: 'Definition',
        awsURL: {
          label: '☁️ Link to AWS',
        },
        resourceCode: { label: '📂 Resource definition' },
        codeCreated: { label: '✨ Code creation date' },
        codeUpdated: { label: '🔄 Code last update' },
        moduleCode: { label: '📂 Module definition' },
        noCode: {
          label: '📂 Code definition',
          value: {
            notManaged: '⚠️ Unmanaged',
            na: '-',
          },
        },
      },
      linkToCoverage: {
        title: 'Cloud-to-code coverage',
        text: 'This resource was directly scanned in the cloud thanks to our new IAM engine.',
        link: 'Find detailled data about its IAC coverage, impacts and dependencies.',
      },
    },
    history: {
      title: 'Time machine',
      timeline: {
        current: 'Current state',
        createdOnCloud: 'Resource created on the Cloud',
        resourceDeletedOnCloud: 'Resource deleted on the Cloud',
        deleted: {
          code: 'Deleted from TF',
          cloud: 'Deleted from cloud',
        },
        detectedByAnyshift: 'Change detected by Anyshift',
        noChanges: 'No changes',
        updatedProperties:
          '{count} {count, plural, one {property} other {properties}} updated',
        newDownstreamImpacts:
          '{count} new downstream {count, plural, one {impact} other {impacts}}',
        deletedDownstreamImpacts:
          '{count} downstream {count, plural, one {impact} other {impacts}} deleted',
        newUpstreamImpacts:
          '{count} new upstream {count, plural, one {impact} other {impacts}}',
        deletedUpstreamImpacts:
          '{count} upstream {count, plural, one {impact} other {impacts}} deleted',
        createdOnTf: 'Source code added',
        updatedOnTf: 'Source code updated',
        deletedOnTf: 'Source code deleted',
        commitPushedOnRepository: '{sha} pushed on {repository}',
        timestamp: {
          header: '🕒 Timestamp',
        },
        updatedBy: {
          header: '👤 Updated by',
        },
        changes: {
          header: '🔎 Changes',
        },
        trigger: {
          header: '💥 Trigger',
        },
        switchToManaged: 'Resource is now managed',
        switchToUnmanaged: 'Resource is now unmanaged',
      },
    },
    updatesPeriod: {
      history: {
        noResults: 'No changes during this period',
      },
      impacts: {
        noResults: 'No impacted resources were updated during this period',
      },
      form: {
        title: 'Visualize updates in time interval:',
        clear: 'Clear',
        submit: 'Go!',
      },
    },
  },
  newProject: {
    success: 'Project created successfully',
    loader: {
      title: 'Creating your project',
      subtitle: 'Do not close the tab',
    },
  },
  profile: {
    title: 'User account settings',
    email: 'Email address',
    updatePassword: {
      title: 'Change password',
      subtitle: 'Enter your new password below',
      form: {
        password: {
          label: 'New password',
          placeholder: '••••••••',
          error: 'Should be at least {min} characters',
        },
        submit: {
          label: 'Update password',
          pending: 'Updating password...',
          error: 'Failed to update password, try again',
          success: 'Password updated',
        },
      },
    },
  },
  coverage: {
    title: 'Cloud-to-code IaC coverage',
    overview: {
      charts: {
        managed:
          '{count, plural, one {{count} managed resource} other {{count} managed resources}}',
        unmanaged:
          '{count, plural, one {{count} unmanaged resource} other {{count} unmanaged resources}}',
      },
      table: {
        resourceType: {
          header: '📦 Resource type',
        },
        resourceId: { header: '🧩 Resource' },
        impactZone: {
          header: '💥 Impact zone',
          options: {
            [IMPACTS_MANAGED]:
              '{count, plural, one {🥊 Impacts {count} managed resource} other {🥊 Impacts {count} managed IAM resources}}',
            [IMPACTED_BY_UNMANAGED]:
              '{count, plural, one {🎯 Impacted by {count} unmanaged resource} other {🎯 Impacted by {count} unmanaged IAM resources}}',
          },
          headerOptions: {
            [IMPACTS_MANAGED]: '🥊 Impacts managed IAM resources',
            [IMPACTED_BY_UNMANAGED]: '🎯 Impacted by unmanaged IAM resources',
          },
        },
        updatedAt: {
          header: '🔄 Last update',
        },
        awsUrl: {
          header: '☁️ Link to AWS',
          cell: 'Open',
        },
        codeUrl: {
          header: '📂 Link to code',
          resource: 'Resource',
          module: 'Module',
          unmanaged: '⚠️ Unmanaged',
          headerOptions: {
            [MANAGED]: '✅ Managed',
            [NOT_MANAGED]: '⚠️ Unmanaged',
          },
        },
        impactType: {
          header: '💥 Impact type',
          options: {
            upstream: '🎯 Upstream',
            downstream: '🥊 Downstream',
          },
        },
        updates: {
          header: '🔄 Updates',
        },
      },
    },
    resourceTypes: {
      iam: {
        [IAM_ROLE.resourceType]: 'IAM Role',
        [IAM_POLICY.resourceType]: 'IAM policy',
        [IAM_USER.resourceType]: 'IAM User',
        [IAM_GROUP.resourceType]: 'IAM Group',
      },
      ec2: {
        [EC2_ELASTIC_IP.resourceType]: 'EC2 Elastic IP',
        [EC2_IMAGE.resourceType]: 'EC2 Image',
        [EC2_INSTANCE.resourceType]: 'EC2 Instance',
        [EC2_INTERNET_GATEWAY.resourceType]: 'EC2 Internet Gateway',
        [EC2_KEY_PAIR.resourceType]: 'EC2 Key Pair',
        [EC2_LAUNCH_TEMPLATE.resourceType]: 'EC2 Launch Template',
        [EC2_LOAD_BALANCER.resourceType]: 'EC2 Load Balancer',
        [EC2_NETWORK_ACL.resourceType]: 'EC2 Network ACL',
        [EC2_NETWORK_INTERFACE.resourceType]: 'EC2 Network Interface',
        [EC2_PLACEMENT_GROUP.resourceType]: 'EC2 Placement Group',
        [EC2_ROUTE_TABLE.resourceType]: 'EC2 Route Table',
        [EC2_SECURITY_GROUP.resourceType]: 'EC2 Security Group',
        [EC2_SNAPSHOT.resourceType]: 'EC2 Snapshot',
        [EC2_VPC.resourceType]: 'EC2 VPC',
        [EC2_SUBNET.resourceType]: 'EC2 Subnet',
        [EC2_VOLUME.resourceType]: 'EC2 Volume',
      },
      lambda: {
        [LAMBDA_ALIAS.resourceType]: 'Lambda Alias',
        [LAMBDA_EVENT_SOURCE_MAPPING.resourceType]:
          'Lambda Event Source Mapping',
        [LAMBDA_FUNCTION.resourceType]: 'Lambda Function',
        [LAMBDA_LAYER.resourceType]: 'Lambda Layer',
        [LAMBDA_VERSION.resourceType]: 'Lambda Version',
      },
      rds: {
        [RDS_DB_CLUSTER.resourceType]: 'RDS Cluster',
        [RDS_DB_CLUSTER_PARAMETER_GROUP.resourceType]:
          'RDS Cluster Parameter Group',
        [RDS_DB_INSTANCE.resourceType]: 'RDS Instance',
        [RDS_DB_PARAMETER_GROUP.resourceType]: 'RDS DB Parameter Group',
        [RDS_DB_PROXY.resourceType]: 'RDS Proxy',
        [RDS_DB_SECURITY_GROUP.resourceType]: 'RDS Security Group',
        [RDS_DB_SUBNET_GROUP.resourceType]: 'RDS Subnet Group',
        [RDS_OPTION_GROUP.resourceType]: 'RDS Option Group',
      },
      route53: {
        [ROUTE53_CIDR.resourceType]: 'Route53 CIDR',
        [ROUTE53_HEALTH_CHECK.resourceType]: 'Route53 Health Check',
        [ROUTE53_HOSTED_ZONE.resourceType]: 'Route53 Hosted Zone',
        [ROUTE53_QUERY_LOGGING_CONFIG.resourceType]:
          'Route53 Query Logging Config',
        [ROUTE53_REUSABLE_DELEGATION_SET.resourceType]:
          'Route53 Reusable Delegation Set',
        [ROUTE53_TRAFFIC_POLICY.resourceType]: 'Route53 Traffic Policy',
        [ROUTE53_TRAFFIC_POLICY_INSTANCE.resourceType]:
          'Route53 Traffic Policy Instance',
      },
      s3: {
        [S3_ACCESS_POINT.resourceType]: 'S3 Access Point',
        [S3_BUCKET.resourceType]: 'S3 Bucket',
      },
    },
    emojiResourceTypes: {
      iam: {
        [IAM_ROLE.resourceType]: '🎭 IAM Role',
        [IAM_POLICY.resourceType]: '🎟️ IAM Policy',
        [IAM_USER.resourceType]: '👤 IAM User',
        [IAM_GROUP.resourceType]: '👥 IAM Group',
      },
      ec2: {
        [EC2_ELASTIC_IP.resourceType]: '📡 EC2 Elastic IP',
        [EC2_IMAGE.resourceType]: '🖼️ EC2 Image',
        [EC2_INSTANCE.resourceType]: '🖥️ EC2 Instance',
        [EC2_INTERNET_GATEWAY.resourceType]: '🚪 EC2 Internet Gateway',
        [EC2_KEY_PAIR.resourceType]: '🔑 EC2 Key Pair',
        [EC2_LAUNCH_TEMPLATE.resourceType]: '📜 EC2 Launch Template',
        [EC2_LOAD_BALANCER.resourceType]: '⚖️ EC2 Load Balancer',
        [EC2_NETWORK_ACL.resourceType]: '🚦 EC2 Network ACL',
        [EC2_NETWORK_INTERFACE.resourceType]: '🔌 EC2 Network Interface',
        [EC2_PLACEMENT_GROUP.resourceType]: '📍 EC2 Placement Group',
        [EC2_ROUTE_TABLE.resourceType]: '🛣️ EC2 Route Table',
        [EC2_SECURITY_GROUP.resourceType]: '🛡️ EC2 Security Group',
        [EC2_SNAPSHOT.resourceType]: '📸 EC2 Snapshot',
        [EC2_VPC.resourceType]: '🏢 EC2 VPC',
        [EC2_SUBNET.resourceType]: '🌐 EC2 Subnet',
        [EC2_VOLUME.resourceType]: '💾 EC2 Volume',
      },
      lambda: {
        [LAMBDA_ALIAS.resourceType]: '🔗 Lambda Alias',
        [LAMBDA_EVENT_SOURCE_MAPPING.resourceType]:
          '🔄 Lambda Event Source Mapping',
        [LAMBDA_FUNCTION.resourceType]: '⚡ Lambda Function',
        [LAMBDA_LAYER.resourceType]: '📦 Lambda Layer',
        [LAMBDA_VERSION.resourceType]: '🔢 Lambda Version',
      },
      rds: {
        [RDS_DB_CLUSTER.resourceType]: '🗄️ RDS Cluster',
        [RDS_DB_CLUSTER_PARAMETER_GROUP.resourceType]: '⚙️ RDS Parameter Group',
        [RDS_DB_INSTANCE.resourceType]: '🖥️ RDS Instance',
        [RDS_DB_PARAMETER_GROUP.resourceType]: '⚙️ RDS Parameter Group',
        [RDS_DB_PROXY.resourceType]: '🔀 RDS Proxy',
        [RDS_DB_SECURITY_GROUP.resourceType]: '🛡️ RDS Security Group',
        [RDS_DB_SUBNET_GROUP.resourceType]: '🌐 RDS Subnet Group',
        [RDS_OPTION_GROUP.resourceType]: '🛠️ RDS Option Group',
      },
      route53: {
        [ROUTE53_CIDR.resourceType]: '📏 Route53 CIDR',
        [ROUTE53_HEALTH_CHECK.resourceType]: '💓 Route53 Health Check',
        [ROUTE53_HOSTED_ZONE.resourceType]: '🌍 Route53 Hosted Zone',
        [ROUTE53_QUERY_LOGGING_CONFIG.resourceType]:
          '📜 Route53 Query Logging Config',
        [ROUTE53_REUSABLE_DELEGATION_SET.resourceType]:
          '🔁 Route53 Reusable Delegation Set',
        [ROUTE53_TRAFFIC_POLICY.resourceType]: '🚦 Route53 Traffic Policy',
        [ROUTE53_TRAFFIC_POLICY_INSTANCE.resourceType]:
          '📍 Route53 Traffic Policy Instance',
      },
      s3: {
        [S3_ACCESS_POINT.resourceType]: '🔗 S3 Access Point',
        [S3_BUCKET.resourceType]: '️🪣 S3 Bucket',
      },
    },
    details: {
      linkToAWS: 'Link to AWS',
      impactAnalysis: {
        noImpact:
          'This {managed, select, true {managed} other {unmanaged}} resource {verb} IAM resources',
        impactsManaged:
          'This {managed, select, true {managed} other {unmanaged}} resource {verb} {managedCount, plural, one {{managedCount} managed IAM resource} other {{managedCount} managed IAM resources}}',
        impactsUnmanaged:
          'This {managed, select, true {managed} other {unmanaged}} resource {verb} {unmanagedCount, plural, one {{unmanagedCount} unmanaged IAM resource} other {{unmanagedCount} unmanaged IAM resources}}',
        impactsBoth:
          'This {managed, select, true {managed} other {unmanaged}} resource {verb} {managedCount, plural, one {{managedCount} managed IAM resource} other {{managedCount} managed IAM resources}} and {unmanagedCount, plural, one {{unmanagedCount} unmanaged IAM resource} other {{unmanagedCount} unmanaged IAM resources}}',
        downstream: {
          label: '🥊 Downstream impact',
        },
        upstream: {
          label: '🎯 Upstream impact',
        },
        title: 'Impact analysis',
        managedBy: '• This resource is managed in terraform by ',
        unmanaged: "• This resource isn't managed",
      },
      statistics: {
        title: 'Statistics',
        createdAt: '✨ Creation date',
        updatedAt: '🔄 Last update',
        coverage: ' IAC coverage',
      },
      impactNotAvailable: {
        title: 'Impact analysis',
        text1:
          '• Cloud Impact analysis does not support resources from "{service}" service yet',
        text2: '• New services are on the roadmap, contact us at',
        text3: 'if you want yours done first!',
      },
      impacts: 'Impacted resources',
      intervalFromTo: '{prefix} between {from} and {to}',
      intervalFrom: '{prefix} since {from}',
      intervalTo: '{prefix} until {to}',
    },
  },
  search: {
    title: 'Cloud-to-Code Search',
    form: {
      searchTerm: {
        placeholder: 'Search for any cloud resource',
      },
      submit: {
        label: 'Search',
        pending: 'Searching...',
      },
    },
    table: {
      awsUrl: {
        header: '☁️ Link to AWS',
        cell: 'Open',
      },
      codeUrl: {
        header: '📂 Link to code',
        cell: 'Open',
        resource: 'Resource',
        module: 'Module',
        unmanaged: '⚠️ Unmanaged',
      },
      codeLastUpdatedAt: {
        header: '🔄 Last update',
      },
      codeLastUpdatedBy: {
        header: '👤 Updated by',
      },
      numberOfUpdates: {
        header: '🔄 Updates',
      },
      resourceType: {
        header: '📦 Resource type',
      },
      resourceId: {
        header: '🧩 Resource',
      },
      account: {
        header: '👤 Account',
      },
      region: {
        header: '🌎 Region',
      },
      service: {
        header: '🛠️ Service',
      },
      matchedProperties: {
        header: '🔎 Matched properties',
      },
      deleted: {
        header: '🗑️ Status',
        options: {
          deleted: '🔴 Deleted',
          active: '🟢 Active',
        },
      },
    },
    changes: {
      title: 'Search for updates between:',
      clear: 'Clear',
      showDeleted: 'Show resources deleted during the selected interval',
    },
  },
  integrations: {
    title: 'Add data to Anyshift',
    subtitle: 'Integrations',
    categories: {
      scm: {
        title: 'SCM',
        github: 'Github',
        gitlab: 'Gitlab',
      },
      publicCloud: {
        title: 'Public Cloud',
        aws: 'AWS',
        gcp: 'GCP',
      },
      monitoring: {
        title: 'Monitoring',
        datadog: 'Datadog',
      },
      containers: {
        title: 'Containers',
        kubernetes: 'Kubernetes',
      },
      terraformBackend: {
        title: 'Terraform Backend',
        s3: 'S3',
        hcp: 'HCP',
      },
      incidentsManagement: {
        title: 'Incidents Management',
        pagerduty: 'PagerDuty',
      },
    },
    scm: {
      tryIt:
        'Code data is now connected with Anyshift<button>Try it now!</button>',
    },
    aws: {
      title: 'Integrate with AWS',
      cta: 'Anyshift needs access to AWS to periodically scan your cloud resources. You can give Anyshift access to your AWS by creating an IAM user or an IAM role.{br}Ideally, use the <a>ReadOnlyAccess</a> policy.',
      tryIt:
        'AWS Cloud data is now connected with Anyshift<button>Try it now!</button>',
      syncComplete: 'AWS cloud data is now connected with Anyshift',
      roles: {
        title: 'IAM Roles',
        new: 'New role',
        table: {
          name: {
            header: 'Role name',
          },
          edit: 'Edit role',
          delete: 'Delete role',
          status: {
            header: 'Status',
          },
        },
      },
      users: {
        title: 'IAM Users',
        new: 'New user',
        table: {
          name: {
            header: 'User name',
          },
          edit: 'Edit user',
          delete: 'Delete user',
          status: {
            header: 'Status',
          },
        },
      },
      status: {
        active: '🟢 Active',
        inactive: '🔄 Syncing',
        error: '🔴 Failed to fetch AWS resources',
      },
      roleError:
        'Anyshift failed to assume this role to fetch AWS resources, check if the role exists, its trust relationship policy and the permissions policy attached to it.',
      userError:
        'Anyshift failed to use these credentials to fetch AWS resources, check if the user exists and the permissions policy attached to it.',
      warningRole: '• One of the roles failed to fetch AWS resources',
      warningUser: '• One of the users failed to fetch AWS resources',
    },
    gcp: {
      title: 'Integrate with GCP',
      cta: 'Anyshift needs access to GCP to periodically scan your cloud resources. You can give Anyshift access to your GCP by creating a service account.{br}Use a service account with the <a>Viewer role</a> for read-only access.',
      tryIt:
        'GCP Cloud data is now connected with Anyshift<button>Try it now!</button>',
      syncComplete: 'GCP cloud data is now connected with Anyshift',
      apiNote: {
        title: 'Important:',
        description:
          'Even if your service account has the necessary permissions, Anyshift will not be able to access GCP services unless the corresponding APIs are enabled in your GCP project. Please ensure all required APIs are enabled in the Google Cloud Console.',
        documentation:
          'To get more information about accesses and API, <a>read our documentation</a>.',
      },
      serviceAccounts: {
        title: 'Service Accounts',
        new: 'New service account',
        table: {
          name: {
            header: 'Email',
          },
          projectId: {
            header: 'Project ID',
          },
          edit: 'Edit service account',
          delete: 'Delete service account',
          status: {
            header: 'Status',
          },
        },
        delete: {
          confirm: {
            title: 'Delete Service Account',
            body: 'Are you sure you want to delete this service account?',
          },
        },
      },
      status: {
        active: '🟢 Active',
        inactive: '🔄 Syncing',
        error: '🔴 Failed to fetch GCP resources',
      },
      serviceAccountError:
        'Anyshift failed to use this service account to fetch GCP resources, check if the service account exists and has the required permissions.',
      warningServiceAccount:
        '• One of the service accounts failed to fetch GCP resources',
    },
    terraformBackend: {
      buckets: {
        title: 'Integrate with your Terraform state',
        cta: 'Link your cloud resources with their code definition by providing us with your Terraform state files.',
        list: {
          title: 'S3 Buckets',
          table: {
            name: {
              header: 'Name',
            },
            region: {
              header: 'Region',
            },
            status: {
              header: 'Status',
              options: {
                active: '🟢 Active',
                synced: '🔄 Syncing',
                'unsynced-with-cloud': '🟠 Not found on the cloud',
                'no-creds': '🔴 No credentials able to access bucket content',
                error: '🔴 Credentials failed to access bucket content',
              },
              'unsynced-with-cloud':
                'This bucket is configured as the backend of one of your Terraform directories, but we could not find it on AWS.{br}Update your credentials or add new ones to complete the integration.',
              error:
                'This bucket is configured as the backend of one of your Terraform directories, we found it on AWS, but the following credentials failed to access its content:{creds}{br}Update your credentials or add new ones to complete the integration.',
              'no-creds':
                'This bucket is configured as the backend of one of your Terraform directories, we found it on AWS, but we could not find any credentials able to access its content.{br}Update your credentials or add new ones to complete the integration.',
            },
          },
        },
        warningUnsynced:
          '• Some buckets referenced in your code were not found on AWS',
        warningAccess:
          '• Your AWS credentials failed to access some buckets content',
      },
      hcpCloud: {
        title: 'Integrate with HCP Cloud',
        cta: 'Link your cloud resources with their code definition by providing us with the state files found in your HCP Cloud workspaces.',
        tryIt:
          'Terraform states data is now connected with Anyshift<button>Try it now!</button>',
        list: {
          title: 'HCP Cloud tokens',
          new: 'New token',
          table: {
            name: {
              header: 'Name',
            },
            url: {
              header: 'HCP Cloud URL',
            },
            workspace: {
              header: 'Workspace',
            },
            edit: 'Edit token',
            delete: 'Delete token',
            status: {
              header: 'Status',
              options: {
                active: '🟢 Active',
                inactive: '🔄 Syncing',
                error: '🔴 Failed to acces HCP Cloud workspace',
              },
              error: {
                tooltip:
                  'This token failed to access the HCP Cloud workspace, check if the token is valid and has access to the workspace.',
              },
            },
          },
          tokenDeleted: 'HCP Cloud token deleted',
        },
        warningError: 'Some tokens failed to access HCP Cloud workspaces',
        form: {
          title: 'HCP Cloud token',
          subtitle: 'Create or update your HCP Cloud tokens',
          name: {
            label: 'Display name',
            placeholder: 'Prod token',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          url: {
            label: 'HCP Cloud URL',
            placeholder: 'https://app.terraform.io',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          workspace: {
            label: 'Workspace',
            placeholder: 'organization/workspace',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          token: {
            label: 'Token',
            placeholder: 'Secret token',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          create: {
            label: 'Save HCP Cloud token',
            pending: 'Saving...',
            error: 'Failed to save HCP Cloud token, try again',
            success: 'HCP Cloud token saved',
          },
          update: {
            label: 'Update HCP Cloud token',
            pending: 'Updating...',
            error: 'Failed to update HCP Cloud token, try again',
            success: 'HCP Cloud token updated',
          },
          delete: {
            label: 'Delete HCP Cloud token',
            pending: 'Deleting...',
            error: 'Failed to delete HCP Cloud token, try again',
            success: 'HCP Cloud token deleted',
          },
        },
      },
    },
    containers: {
      kubernetes: {
        warningStale: '• Some clusters have not sent a heartbeat recently',
        title: 'Integrate with Kubernetes',
        cta: 'Monitor your Kubernetes resources in real-time. Install the Anyshift Agent to connect your clusters and track changes automatically.',
        clusters: {
          title: 'Kubernetes Clusters',
          table: {
            cluster_name: {
              header: 'Cluster Name',
            },
            cluster_id: {
              header: 'Cluster ID',
            },
            agent_version: {
              header: 'Agent Version',
            },
            last_heartbeat: {
              header: 'Last Heartbeat',
            },
            init: {
              header: 'Agent Initialized',
            },
            status: {
              header: 'Status',
              options: {
                active: '🟢 Active',
                stale: '🟠 Stale',
                error: '🔴 Error',
              },
            },
          },
        },
        tokenGeneration: {
          title: '🔑 Generate Agent Token',
          description:
            'Generate a secure token for your Kubernetes cluster agent. Each cluster needs its own unique token.',
          clusterNameLabel: 'Cluster Name',
          clusterNamePlaceholder: 'e.g., production, staging, dev-cluster',
          clusterNameHelper:
            'Use a logical name to identify your cluster (e.g., production, staging, dev)',
          generateButton: 'Generate Token',
          generatingButton: 'Generating...',
          generatedTokenLabel: 'Generated Token',
          tokenHelper:
            "Use this token in your Kubernetes agent configuration. Keep it secure and don't share it publicly.",
          tokenGeneratedSuccess: 'Token generated successfully',
          tokenGeneratedSuccessDescription:
            'Your Kubernetes agent token has been generated.',
          tokenGenerationError: 'Failed to generate token',
          tokenGenerationErrorDescription:
            'Please try again or contact support.',
          tokenCopiedSuccess: 'Token copied to clipboard',
          tokenCopiedSuccessDescription:
            'Use this token to configure your Kubernetes agent.',
          tokenCopyError: 'Failed to copy token',
          tokenCopyErrorDescription: 'Please copy the token manually.',
        },
        installationSteps: {
          title: 'Installation Steps',
          step1: 'Generate a token using the form above',
          step2: 'Install the Anyshift Kubernetes agent in your cluster',
          step3: 'Configure the agent with your generated token',
          step4: 'Your cluster will appear in the table below once connected',
          documentation: 'View Installation Guide',
        },
        warningUnsynced:
          '• Some buckets referenced in your code were not found on AWS',
        warningAccess:
          '• Your AWS credentials failed to access some buckets content',
      },
    },

    incidentsManagement: {
      pagerduty: {
        title: 'Integrate with PagerDuty',
        cta: 'PagerDuty tokens allow Anyshift to access your incidents, acknowledge and leave comments on them. You can also configure a PagerDuty webhook so that Anyshift can receive real-time updates on your incidents',
        tryIt:
          'PagerDuty data is now connected with Anyshift<button>Try it now!</button>',
        list: {
          title: 'PagerDuty tokens',
          new: 'New token',
          table: {
            name: {
              header: 'Name',
            },
            edit: 'Edit token',
            delete: 'Delete token',
            status: {
              header: 'Status',
              options: {
                active: '🟢 Active',
                error: '🔴 Failed to access PagerDuty API',
              },
              error: {
                tooltip:
                  'This token failed to access the PagerDuty API, check if the token is valid and has access to the PagerDuty API.',
              },
            },
          },
          tokenDeleted: 'PagerDuty token deleted',
        },
        form: {
          title: 'PagerDuty token',
          subtitle: 'Add or update your PagerDuty API tokens',
          name: {
            label: 'Display name',
            placeholder: 'Production PagerDuty',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          token: {
            label: 'API Token',
            placeholder: 'Enter your PagerDuty API token',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          create: {
            label: 'Save PagerDuty token',
            pending: 'Saving...',
            error: 'Failed to save PagerDuty token, try again',
            success: 'PagerDuty token saved',
          },
          update: {
            label: 'Update PagerDuty token',
            pending: 'Updating...',
            error: 'Failed to update PagerDuty token, try again',
            success: 'PagerDuty token updated',
          },
          delete: {
            label: 'Delete PagerDuty token',
            pending: 'Deleting...',
            error: 'Failed to delete PagerDuty token, try again',
            success: 'PagerDuty token deleted',
          },
        },
        webhooks: {
          title: 'PagerDuty webhooks',
          cta: 'Configure PagerDuty webhooks to receive real-time incident updates in Anyshift.',
          tryIt:
            'PagerDuty webhook is now connected with Anyshift<button>Try it now!</button>',
          list: {
            title: 'PagerDuty webhooks',
            new: 'New webhook',
            table: {
              name: {
                header: 'Name',
              },
              edit: 'Edit webhook',
              delete: 'Delete webhook',
              status: {
                header: 'Status',
                options: {
                  active: '🟢 Active',
                  error: '🔴 Failed to configure webhook',
                  pending: '🔄 Webhook not called yet',
                },
                error: {
                  tooltip:
                    'This webhook failed to configure properly, check if the secret key is valid and webhook settings are correct.',
                },
                pending: {
                  tooltip:
                    "The webhook wasn't called by PagerDuty yet. Click 'Send Test Event' on PagerDuty to manually check its status, or wait for the next incident update.",
                },
              },
            },
            webhookDeleted: 'PagerDuty webhook deleted',
          },
          form: {
            title: 'PagerDuty webhook',
            subtitle: 'Create or update your PagerDuty webhook configurations',
            cta: 'Create a webhook in Pagerduty (Integrations > Generic Webhooks v3 menu), and copy the secret key here.{br}{br}Webhook URL:',
            name: {
              label: 'Display name',
              placeholder: 'Production PagerDuty Webhook',
              errors: {
                min: 'Should be at least {min} characters',
                max: 'Should be at most {max} characters',
              },
            },
            secretKey: {
              label: 'Webhook Secret Key',
              placeholder: 'Enter your PagerDuty webhook secret key',
              errors: {
                min: 'Should be at least {min} characters',
                max: 'Should be at most {max} characters',
              },
            },
            create: {
              label: 'Create PagerDuty webhook',
              pending: 'Creating...',
              error: 'Failed to create PagerDuty webhook, try again',
              success: 'PagerDuty webhook created',
            },
            update: {
              label: 'Update PagerDuty webhook',
              pending: 'Updating...',
              error: 'Failed to update PagerDuty webhook, try again',
              success: 'PagerDuty webhook updated',
            },
            delete: {
              label: 'Delete PagerDuty webhook',
              pending: 'Deleting...',
              error: 'Failed to delete PagerDuty webhook, try again',
              success: 'PagerDuty webhook deleted',
            },
          },
        },
      },
    },

    monitoring: {
      datadog: {
        title: 'Integrate with Datadog',
        cta: 'Plug live data from Datadog into Anyshift to get real-time alerts and insights on your cloud resources.',
        tryIt:
          'Datadog data is now connected with Anyshift<button>Try it now!</button>',
        list: {
          title: 'Datadog API credentials',
          new: 'New API credentials',
          table: {
            name: {
              header: 'Name',
            },
            site: {
              header: 'Datadog site url',
            },
            edit: 'Edit credentials',
            delete: 'Delete credentials',
            status: {
              header: 'Status',
              options: {
                active: '🟢 Active',
                inactive: '🔄 Syncing',
                error: '🔴 Failed to acces Datadog API',
              },
              error: {
                tooltip:
                  'This token failed to access the Datadog API, check if the token is valid and has access to the Datadog API.',
              },
            },
          },
          apiCredentialsDeleted: 'API credentials deleted',
          newDisabled:
            'For now, only one Datadog API key per project is supported. Please delete the existing one to create a new one.',
        },
        warningError: 'Some API credentials failed to access Datadog API',
        form: {
          title: 'Datadog API credentials',
          subtitle: 'Add or update your Datadog API credentials',
          name: {
            label: 'Display name',
            placeholder: 'Prod credentials',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          site: {
            label: 'Datadog site URL',
            placeholder: 'datadoghq.com',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          apiKey: {
            label: 'API key',
            placeholder: 'API key',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          appKey: {
            label: 'Application key',
            placeholder: 'Application key',
            errors: {
              min: 'Should be at least {min} characters',
              max: 'Should be at most {max} characters',
            },
          },
          create: {
            label: 'Save Datadog API credentials',
            pending: 'Saving...',
            error: 'Failed to save Datadog API credentials, try again',
            success: 'Datadog API credentials saved',
          },
          update: {
            label: 'Update Datadog API credentials',
            pending: 'Updating...',
            error: 'Failed to update Datadog API credentials, try again',
            success: 'Datadog API credentials updated',
          },
          delete: {
            label: 'Delete Datadog API credentials',
            pending: 'Deleting...',
            error: 'Failed to delete Datadog API credentials, try again',
            success: 'Datadog API credentials deleted',
          },
        },
      },
    },
    tryIt: {
      title: 'Try it!',
      connect: 'Connect',
      import: 'Connect {value}',
      examples: {
        hardcodedValues: 'Find hardcoded values in my IAC',
        vpcIdList:
          'Give me the VPC IDs and CIDR of each default VPCs and subnets',
        administratorAccessWithCode:
          'List all IAM users with AdministratorAccess Policy and their link to the code',
        anyshiftAgentAccess:
          'What access does the kubernetes anyshift-agent have on my cluster?',
      },
    },
  },
  configuration: {
    title: 'Project configuration - {projectName}',
    githubSettings: {
      title: 'Github integration',
      button: 'Install github app',
      repositories:
        '{count, plural, =0 {No repositories ⚠️} one {{count} repository} other {{count} repositories}}',
      error: 'Failed to fetch Github configurations',
    },
    awsCredentials: {
      title: 'AWS integration',
      button: 'Add AWS credentials',
      form: {
        title: 'AWS credentials',
        accountName: {
          label: 'Account name',
          placeholder: 'Account name',
          errors: {
            min: 'Should be at least {min} characters',
            max: 'Should be at most {max} characters',
          },
        },
        accessKey: {
          label: 'Access key',
          placeholder: 'Access key',
          errors: {
            min: 'Should be at least {min} characters',
            max: 'Should be at most {max} characters',
          },
        },
        secretKey: {
          label: 'Secret key',
          placeholder: 'Secret key',
          errors: {
            min: 'Should be at least {min} characters',
            max: 'Should be at most {max} characters',
          },
        },
        create: {
          label: 'Save AWS credentials',
          pending: 'Saving...',
          error: 'Failed to save AWS credentials, try again',
          success: 'AWS credentials saved',
        },
        update: {
          label: 'Update AWS credentials',
          pending: 'Updating...',
          error: 'Failed to update AWS credentials, try again',
          success: 'AWS credentials updated',
        },
        delete: {
          label: 'Delete AWS credentials',
          pending: 'Deleting...',
          error: 'Failed to delete AWS credentials, try again',
          success: 'AWS credentials deleted',
        },
      },
      error: 'Failed to fetch AWS credentials',
    },
    awsRole: {
      title: 'AWS integration',
      button: 'Add AWS role',
      form: {
        title: 'AWS role',
        trustRelationShip: 'Trust policy to add to your role',
        name: {
          label: 'Display name',
          placeholder: 'Role display name',
          errors: {
            min: 'Should be at least {min} characters',
            max: 'Should be at most {max} characters',
          },
        },
        arn: {
          label: 'ARN',
          placeholder: 'Role ARN',
          errors: {
            min: 'Should be at least {min} characters',
            max: 'Should be at most {max} characters',
          },
        },
        externalId: {
          label: 'External ID (optional, see trust policy)',
          placeholder: 'Role external ID',
          errors: {
            max: 'Should be at most {max} characters',
          },
        },
        create: {
          label: 'Save AWS role',
          pending: 'Saving...',
          error: 'Failed to save AWS role, try again',
          success: 'AWS role saved',
        },
        update: {
          label: 'Update AWS role',
          pending: 'Updating...',
          error: 'Failed to update AWS role, try again',
          success: 'AWS role updated',
        },
        delete: {
          label: 'Delete AWS role',
          pending: 'Deleting...',
          error: 'Failed to delete AWS role, try again',
          success: 'AWS role deleted',
        },
      },
      error: 'Failed to fetch AWS roles',
    },
    gcpServiceAccount: {
      title: 'GCP integration',
      button: 'Add GCP service account',
      form: {
        title: 'GCP Service Account',
        subtitle: 'Add or edit GCP service account credentials',
        manageCredentials: 'Manage Credentials',
        updateDetails: 'Update Credentials',
        enterDetails: 'Enter Details',
        serviceAccountName: {
          label: 'Service account name',
          placeholder: 'e.g., production-gcp',
          errors: {
            min: 'Should be at least {min} characters',
            max: 'Should be at most {max} characters',
            duplicate: 'A service account with this name already exists',
          },
        },
        serviceAccountJson: {
          label: 'Service Account JSON',
          placeholder:
            '{\n  "type": "service_account",\n  "project_id": "your-project-id",\n  "private_key_id": "...",\n  "private_key": "...",\n  "client_email": "...",\n  "client_id": "...",\n  "auth_uri": "...",\n  "token_uri": "...",\n  "auth_provider_x509_cert_url": "...",\n  "client_x509_cert_url": "..."\n}',
          help: 'Learn how to <a>create a service account key</a>',
          optional: '(Optional)',
          updateHelp:
            'Leave empty to keep existing credentials, or provide new JSON to update them',
          errors: {
            required: 'Service account JSON is required',
            invalid:
              'Invalid service account JSON. Must include type, project_id, private_key, and client_email',
          },
        },
        fileUpload: {
          dropzone:
            'Drag and drop your service account JSON file here, or click to browse',
          hint: 'JSON files only',
          error: 'Failed to read file. Please ensure it is a valid JSON file',
        },
        or: 'or',
        currentDetails: {
          title: 'Current Service Account Details',
          field: 'Field',
          value: 'Value',
          projectId: 'GCP Project ID',
          clientEmail: 'Client Email',
          clientId: 'Client ID',
          privateKeyId: 'Private Key ID',
          status: 'Status',
          lastUpdated: 'Last Updated',
        },
        create: {
          label: 'Save GCP service account',
          pending: 'Saving...',
          error: 'Failed to save GCP service account',
          success: 'GCP service account saved',
        },
        update: {
          label: 'Update GCP service account',
          pending: 'Updating...',
          error: 'Failed to update GCP service account',
          success: 'GCP service account updated',
        },
        delete: {
          label: 'Delete GCP service account',
          pending: 'Deleting...',
          error: 'Failed to delete GCP service account',
          success: 'GCP service account deleted',
        },
      },
      error: 'Failed to fetch GCP service accounts',
    },
    projectManagement: {
      title: 'Project management',
      email: 'Email',
      role: 'Role',
      actions: 'Actions',
      promoteTooltip:
        'Admins can:{br}- Invite new users{br}- Promote members{br}- Delete the project',
      form: {
        email: {
          placeholder: 'Invited user email address',
          error: 'Should be a valid email',
        },
        submit: {
          label: 'Invite user to project',
          pending: 'Inviting user...',
          error: 'Failed to invite user, check the email and try again',
          success: 'User invited',
        },
      },
      inviteUserDialog: {
        title: 'Invite user to project',
        error: 'We encountered an error, please try again',
      },
      signUpAndInviteUserDialog: {
        subtitle:
          'This user does not have an account yet, invite them to join Anyshift?',
        button: {
          label: 'Invite user',
          pending: 'Inviting user...',
          error: 'Failed to invite user, check the email and try again',
          success: 'User invited',
        },
      },
      inviteExistingUserDialog: {
        subtitle:
          'This user already has an account, invite them to join your project?',
        instructions:
          'If the user is already in a project, ask them to leave it first',
        button: {
          label: 'Invite user',
          pending: 'Inviting user...',
          error:
            'Failed to invite user. If the user is already in a project, ask them to leave it first',
          success: 'User invited',
        },
      },
      enablePrImpact: 'Enable PR impact analysis',
      enablePrAiAnalysis: 'Enable AI insights on PR impact analysis',
      enablePrAiAnalysisDisabled: 'Enable PR impact analysis first',
      prImpactUpdated: 'PR settings updated',
    },
    regenerateGraph: {
      title: 'Anyshift Graph',
      button: {
        label: 'Refresh state and cloud in graph',
        pending: 'Refreshing state and cloud in graph...',
        error:
          'Failed to regenerate graph, check your project configuration and try again',
        success: 'State and cloud refreshed',
      },
      timeMachine: {
        button: {
          label: 'Regenerate time machine',
          pending: 'Regenerating time machine...',
          error:
            'Failed to regenerate time machine, check your project configuration and try again',
          success: 'Time machine regenerated',
        },
      },
    },
    dangerZone: {
      title: 'Danger Zone',
      deleteProject: {
        button: 'Delete project',
        dialog: {
          button: {
            label: 'Delete project',
            pending: 'Deleting project...',
            error: 'Failed to delete project, try again',
            success: 'Project deleted',
          },
          title: 'Are you sure?',
          subtitle: 'This action is irreversible',
          cancel: 'Cancel',
        },
      },
      leaveProject: {
        button: 'Leave project',
        dialog: {
          button: {
            label: 'Leave project',
            pending: 'Leaving project...',
            error: 'Failed to leave project, try again',
            success: 'Project left',
          },
          title: 'Are you sure?',
          subtitle:
            "You will lose access to this project, but it won't be deleted",
          cancel: 'Cancel',
        },
        lastAdminTooltip:
          'Promote another member to admin before leaving, or delete the project',
      },
    },
    mcpJwt: {
      title: 'Generate Claude MCP token',
      button: {
        label: 'Generate Token',
        pending: 'Generating...',
        success: 'Token generated and copied to clipboard!',
        error: 'Failed to generate JWT token.',
      },
    },
    slackApp: {
      title: 'Install Anyshift to Slack',
      button: {
        label: 'Add to Slack',
      },
    },
  },
  pullRequests: {
    title: 'Pull Requests',
    noPullRequest: 'No pull requests found',
    impacts:
      '{count, plural, one  {🔴 {count} impact} other  {🔴 {count}  impacts}}',
  },
  slackIntegration: {
    successMessage: 'Your Slack integration has been successfully linked!',
    closePrompt:
      'You can now close this page and return to your Slack workspace.',
    appOpen: 'If Slack does not open automatically, please click',
    here: ' here',
  },
  askAnnie: {
    title: 'Ask Annie',
    thinking: 'Thinking...',
    thinkingWithContent: '{content}',
    welcome: 'How can I help you?',
    placeholder: 'List all IAM users with AdministratorAccess policy',
  },
  catalog: {
    title: 'Use Cases',
    subtitle:
      'Scroll the library, pick a question that matches your use-case, or ask your own.{br}Every example below is wired to real data sources, so what you see is exactly what Annie will pull from her deep knowledge of your infrastructure.',
    tryIt: 'Try it!',
    categories: {
      onboarding: {
        title: 'Onboarding',
        questions: {
          coreServices:
            'Which core services run in production and how are they interconnected?',
          regions:
            'Which regions and availability zones host our production workloads?',
          externalTraffic:
            'Where does external traffic first enter our system?',
        },
      },
      infrastructureManagement: {
        title: 'Infrastructure Management',
        questions: {
          securityGroupImpact:
            'I am changing the security group my_prod_securitygroup attached to EC2 instance server1234. What else will be impacted?',
          terraformModule:
            'I am about to update Terraform module networking vpc. Which EC2 instances depend on it?',
        },
      },
      incidentResponse: {
        title: 'Incident Response & On-call Management',
        questions: {
          dynamoDB: 'What happened to my DynamoDB prod between April 8 and 11?',
          s3Contact: 'Who owns S3 bucket critical-data?',
        },
      },
      accessManagement: {
        title: 'Access Management',
        questions: {
          rdsInstance: "Why can't I access the production RDS instance?",
          iamUsersAdmin:
            'Which IAM users can assume roles with admin privileges and when were their credentials last rotated?',
        },
      },
      securityCompliance: {
        title: 'Security & Compliance',
        questions: {
          sshPermissions:
            'Do we have security groups permitting SSH from 0.0.0.0/0?',
          iamPolicies:
            'Which IAM policies were manually created in the AWS console?',
        },
      },
      networking: {
        title: 'Networking',
        questions: {
          vpcPeering: 'Which VPC peering connections are inactive?',
          hardcodedIPs:
            'Which services have hardcoded IPs in their configurations?',
        },
      },
      costOptimization: {
        title: 'Cost Optimization',
        questions: {
          gpuInstances:
            'Which GPU instances are running outside Terraform management?',
          EBSVolumes:
            'Which EBS volumes are unattached to any running instance?',
        },
      },
    },
  },
  admin: {
    title: 'Admin - Project Integrations List',
    table: {
      projectName: {
        header: 'Project name',
      },
      awsCredentials: {
        header: 'AWS credentials',
      },
      hcpCloudTokens: {
        header: 'HCP Cloud tokens',
      },
      datadogApiCredentials: {
        header: 'Datadog API credentials',
      },
      githubConfigurations: {
        header: 'Github installations',
      },
      s3Buckets: {
        header: 'S3 Buckets',
      },
      id: {
        header: 'Impersonate',
      },
    },
  },
  incidents: {
    title: 'Incidents',
    investigations: {
      title: 'Root Cause Analysis',
    },
    description:
      'All analysis led by Annie are automatically tracked in this view',
    noIncidents: 'No recent root cause analysis',
    autoRefreshing: 'Auto-refreshing...',
    setupToken: 'Manage PagerDuty tokens',
    refresh: {
      success: 'Incidents refreshed successfully',
      error: 'Failed to refresh incidents',
    },
    noFilteredIncidents: 'No incidents match the selected filters',
    showingCount: 'Showing {count} of {total} incidents',
    filters: {
      title: 'Filters',
      clear: 'Clear filters',
      sortLabel: 'Sort by',
      statusLabel: 'Status',
      severityLabel: 'Severity',
      status: {
        all: 'All statuses',
        triggered: 'Triggered',
        acknowledged: 'Acknowledged',
        resolved: 'Resolved',
      },
      severity: {
        all: 'All severities',
        'SEV-1': 'P1 / SEV-1 (Critical)',
        'SEV-2': 'P2 / SEV-2 (High)',
        'SEV-3': 'P3 / SEV-3 (Medium)',
        'SEV-4': 'P4 / SEV-4 (Low)',
        'SEV-5': 'P5 / SEV-5 (Lowest)',
      },
      sort: {
        newest: 'Newest first',
        oldest: 'Oldest first',
        incidentNumber: 'Incident number',
        severity: 'Severity (high to low)',
        status: 'Status (active first)',
      },
    },
    card: {
      started: 'Started',
      resolved: 'Resolved',
      duration: 'Duration',
      ongoing: 'Ongoing incident',
    },
    list: {
      table: {
        incidentNumber: '#',
        title: 'Title',
        summary: 'Summary',
        createdAt: 'Created',
      },
    },
  },
  gettingStarted: {
    index: {
      welcome: {
        title: 'Welcome to Anyshift!',
        subtitle: 'Your AI-powered on-call engineer is ready to help',
      },
      support: {
        text: 'Need help? Contact our support team at',
      },
    },
    welcomeCard: {
      title: 'Meet Annie',
      description1:
        'Annie by Anyshift is your new on-call engineer. It reacts to alerts and incidents to provide root cause analysis in minutes.',
      description2:
        "All interactions with Annie happen right in your team's workspace.",
      features: {
        analysis: 'Instant incident analysis',
        integration: 'Native Slack integration',
        monitoring: '24/7 monitoring support',
      },
      getStarted: {
        title: 'Ready to get started?',
        description:
          'Install the Anyshift app in your Slack workspace to begin working with Annie.',
        button: 'Add to {slack}',
        communityPrefix: 'See Annie in action',
        communityLink: 'on our community workspace',
      },
    },
    slackChannels: {
      title: 'Make Annie On-Call',
      steps: {
        addAnnie: {
          title: 'Add Annie to incidents channels',
          description:
            'This could be #incidents, #alerts, or #on-call channels',
        },
        greetAnnie: {
          title: 'Greet Annie 👋',
          description:
            'Or tag it directly on an alert to start a root cause analysis',
        },
      },
      success: {
        title: 'Annie is now on-call',
        description1:
          'Annie will monitor messages from @PagerDuty and @Datadog.',
        description2: 'It can also track other alerting bots by typing the',
        command: '/register-annie-on-call @user_to_track',
        commandText: 'command.',
        buttonText: 'Review Root Cause Analysis',
      },
      waiting: {
        title: 'Waiting for Annie to Be Ready',
        description: 'Annie is on step away to join your on-call rotation',
        note: 'Automatically checking every few seconds',
        checkNow: 'Check now',
        integrationsPrompt: {
          title: 'Meanwhile, set up integrations',
          description: 'Make Annie smarter about your infrastructure',
          button: 'Set up integrations',
        },
      },
      support: {
        text: 'Need help? Contact us at',
        email: 'support@anyshift.io',
      },
    },
  },
  rca: {
    header: {
      title: 'RCA for Incident #{incidentNumber}',
      duration: 'Total duration: {duration}',
    },
    summary: {
      title: 'Root Cause Analysis',
    },
    steps: {
      title: 'Investigation Steps',
    },
    details: {
      chat: 'Chat',
      noInvestigation: 'No investigation available for this incident',
      noIncidentData: 'No incident data available',
      incidentTitle: 'Incident #{number}',
      viewIn: 'View in {platform}',
      timeline: 'Timeline',
      viewSlackThread: 'View Slack Thread',
      incidentNotFound: 'Incident not found',
      goBack: 'Go back',
      incidentDetails: 'Incident Details',
      investigationTimeline: 'Investigation Timeline',
      confidence: '{confidence}% confidence',
      findings: 'Findings: ',
      duration: 'Duration: ',
      userMessage: 'User Message',
      annieMessage: 'Annie Message',
      user: 'User',
      annie: 'Annie',
    },
    timeline: {
      title: 'Investigation',
      events: 'Timeline',
    },
    chat: {
      title: 'Investigation Chat',
      description: 'Messages will appear in the timeline and on Slack',
      send: 'Send Message',
      placeholder: 'Ask Annie about this investigation...',
      annieResponse:
        "I'm analyzing your question and will provide more details based on the investigation data available.",
    },
    emptyStates: {
      title: 'Annie is eager to investigate its first alert',
      tagAnnie: {
        title: 'Tag Annie in Slack',
        description:
          'When an alert or incident occurs, mention @Annie in your Slack channel to start an investigation.',
        example: '@Annie investigate this alert',
      },
      importPastIncidents: {
        title: 'Import Past Incidents',
        description:
          'Let Annie scan your Slack history to find and investigate past incidents automatically.',
        buttonScanning: 'Scanning...',
        buttonFetch: 'Fetch Past Incidents',
      },
      slackNotInstalled: {
        title: 'Make Annie On-Call',
        description:
          'To start investigating incidents, Annie must join your Slack workspace',
        buttonText: 'Complete Getting Started',
      },
      connectIntegrations: {
        title: '💡 Connect integrations for deeper insights',
        description:
          'Annie provides much better analysis with access to your cloud providers and monitoring tools.',
        aws: 'AWS',
        gcp: 'GCP',
        datadog: 'Datadog',
        seeAll: 'See all',
      },
      toast: {
        scanningTitle: 'Scanning Slack History',
        scanningDescription:
          'Annie is analyzing your Slack channels for past incidents. This may take a few minutes.',
        importCompleteTitle: 'Import Complete',
        importCompleteDescription:
          'Found 3 past incidents. Annie is now investigating them.',
        importFailedTitle: 'Import Failed',
        importFailedDescription:
          'Unable to scan Slack history. Please try again or contact support.',
      },
    },
    status: {
      triggered: {
        label: 'Triggered',
      },
      acknowledged: {
        label: 'Acknowledged',
      },
      resolved: {
        label: 'Resolved',
      },
    },
    investigationStatus: {
      completed: 'Completed',
      investigating: 'Investigating',
      failed: 'Failed',
      timeout: 'Timeout',
    },
  },
  common: {
    back: 'Back',
  },
} as const;
